import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { HelpCircleIcon, PlusIcon } from "../components/Icons";

function CategoryRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get("/vendor/category-requests");
      setRequests(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load category requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <span style={{ background: "#dcfce7", color: "#166534", fontSize: "11px", fontWeight: "800", padding: "3px 8px", borderRadius: "99px" }}>● Approved</span>;
      case "merged":
        return <span style={{ background: "#eff6ff", color: "#1e40af", fontSize: "11px", fontWeight: "800", padding: "3px 8px", borderRadius: "99px" }}>● Merged</span>;
      case "rejected":
        return <span style={{ background: "#fee2e2", color: "#991b1b", fontSize: "11px", fontWeight: "800", padding: "3px 8px", borderRadius: "99px" }}>● Rejected</span>;
      default:
        return <span style={{ background: "#fef3c7", color: "#92400e", fontSize: "11px", fontWeight: "800", padding: "3px 8px", borderRadius: "99px" }}>● Under Review</span>;
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", borderBottom: "1px solid #edf0f4", paddingBottom: "16px" }}>
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: "900", color: "#111315", margin: 0 }}>Category Requests</h2>
          <p style={{ fontSize: "13.5px", color: "var(--text-muted)", margin: "2px 0 0" }}>
            Track the status of custom categories and product classifications you suggested.
          </p>
        </div>
        <Link to="/vendor/products/new" className="btn btn-primary" style={{ fontSize: "12.5px", padding: "8px 18px", gap: "6px" }}>
          <PlusIcon size={16} />
          <span>Add New Product</span>
        </Link>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", color: "#dc2626", padding: "12px 16px", borderRadius: "8px", fontSize: "13.5px" }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: "70px", borderRadius: "10px" }} />)}
        </div>
      ) : requests.length > 0 ? (
        <div style={{ background: "#ffffff", border: "1px solid #e0e4ea", borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#fbfaf8", borderBottom: "1px solid #edf0f4", color: "var(--text-muted)", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>
                  <th style={{ padding: "12px 18px", textAlign: "left" }}>PRODUCT NAME</th>
                  <th style={{ padding: "12px 18px", textAlign: "left" }}>SUGGESTED TAXONOMY</th>
                  <th style={{ padding: "12px 18px", textAlign: "center" }}>STATUS</th>
                  <th style={{ padding: "12px 18px", textAlign: "left" }}>ADMIN NOTES</th>
                  <th style={{ padding: "12px 18px", textAlign: "right" }}>DATE</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req._id} style={{ borderBottom: "1px solid #edf0f4" }}>
                    <td style={{ padding: "14px 18px" }}>
                      <strong style={{ fontSize: "13.5px", color: "#111315" }}>{req.productName}</strong>
                    </td>
                    <td style={{ padding: "14px 18px" }}>
                      <div style={{ fontSize: "13px", color: "#111315", fontWeight: "700" }}>
                        {req.suggestedCategory}
                        {req.suggestedSubcategory && <span> &rsaquo; {req.suggestedSubcategory}</span>}
                        {req.suggestedProductType && <span> &rsaquo; {req.suggestedProductType}</span>}
                      </div>
                      {req.description && (
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)", display: "block", marginTop: "2px" }}>
                          {req.description}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "14px 18px", textAlign: "center" }}>
                      {getStatusBadge(req.status)}
                    </td>
                    <td style={{ padding: "14px 18px", color: "var(--text-mid)" }}>
                      {req.adminNotes || "—"}
                    </td>
                    <td style={{ padding: "14px 18px", textAlign: "right", color: "var(--text-muted)", fontSize: "12px" }}>
                      {new Date(req.createdAt).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#ffffff", border: "1px solid #e0e4ea", borderRadius: "14px" }}>
          <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "#fff5f4", color: "var(--primary)", display: "grid", placeItems: "center", margin: "0 auto 12px" }}>
            <HelpCircleIcon size={26} />
          </div>
          <h4 style={{ fontSize: "16px", fontWeight: "900", color: "#111315", margin: "0 0 6px" }}>No Category Requests</h4>
          <p style={{ fontSize: "13px", color: "var(--text-muted)", maxWidth: "420px", margin: "0 auto 16px" }}>
            When you list an item and cannot find the right category, you can submit a custom category request. It will appear here for tracking.
          </p>
          <Link to="/vendor/products/new" className="btn btn-primary" style={{ fontSize: "12.5px" }}>
            + Create New Product
          </Link>
        </div>
      )}
    </div>
  );
}

export default CategoryRequests;
